import type { MouseEvent, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import TrashAnimatedIcon from '@/components/ui/TrashAnimatedIcon';
import UndoAnimatedIcon from '@/components/ui/UndoAnimatedIcon';

/**
 * ConfirmDeleteModal - Modal padronizado para confirmação de exclusão
 *
 * @param {boolean} isOpen - Controla visibilidade do modal
 * @param {function} onClose - Callback ao fechar/cancelar
 * @param {function} onConfirm - Callback ao confirmar exclusão
 * @param {string} title - Título do modal (ex: "Excluir anexo", "Excluir observação")
 * @param {string} subtitle - Subtítulo opcional (default: "Esta ação não pode ser desfeita")
 * @param {React.ReactNode} children - Conteúdo customizado para mostrar detalhes do item
 * @param {string} confirmText - Texto do botão de confirmação (default: "Excluir")
 * @param {string} cancelText - Texto do botão de cancelar (default: "Cancelar")
 * @param {boolean} loading - Estado de loading durante exclusão
 * @param {string} loadingText - Texto durante loading (default: "Excluindo...")
 *
 * @example
 * <ConfirmDeleteModal
 *   isOpen={showDeleteModal}
 *   onClose={() => setShowDeleteModal(false)}
 *   onConfirm={handleDelete}
 *   title="Excluir observação"
 *   loading={deleting}
 * >
 *   <p>Deseja realmente excluir esta observação?</p>
 * </ConfirmDeleteModal>
 */
export interface ConfirmDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  subtitle?: string;
  children?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  loading?: boolean;
  loadingText?: string;
  /**
   * @deprecated Prop runtime-inert -- accepted for backward-compatibility with
   * legacy call sites (ex: TransitionRuleDrawer, ProjetoDrawer, OportunidadeDrawer)
   * that pass `message` instead of using `children`. The component does NOT
   * render this value; migrate callers to `children` and remove.
   * Tracked in TECHNICAL-DEBT (B.33.2).
   */
  message?: ReactNode;
}

export default function ConfirmDeleteModal({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirmar exclusão',
  subtitle = 'Esta ação não pode ser desfeita',
  children,
  confirmText = 'Excluir',
  cancelText = 'Cancelar',
  loading = false,
  loadingText = 'Excluindo...'
}: ConfirmDeleteModalProps) {
  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm();
  };

  const handleBackdropClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10002
      }}
      onClick={handleBackdropClick}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '450px',
          width: '90%',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="gf-trash-animated" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <TrashAnimatedIcon size={20} />
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#111827' }}>
              {title}
            </h3>
            {subtitle && (
              <p style={{ margin: 0, fontSize: '13px', color: 'var(--muted)' }}>
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {/* Content */}
        {children && (
          <div style={{
            padding: '16px',
            background: '#f9fafb',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            {children}
          </div>
        )}

        {/* Footer */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            className="btn-add undo-animated"
            onClick={onClose}
            disabled={loading}
          >
            <UndoAnimatedIcon size={15} /> <span className="btn-text">{cancelText}</span>
          </button>
          <button
            className="btn-add gf-trash-animated"
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? (
              <>
                <div style={{
                  width: '14px',
                  height: '14px',
                  border: '2px solid rgba(239, 68, 68, 0.3)',
                  borderTop: '2px solid #ef4444',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  marginRight: '6px'
                }} />
                {loadingText}
              </>
            ) : (
              <>
                <TrashAnimatedIcon size={14} />
                <span className="btn-text">{confirmText}</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Keyframe para spinner */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>,
    document.body
  );
}

/**
 * FileDeleteContent - Componente auxiliar para exibir detalhes de arquivo no modal
 *
 * @param {string} fileName - Nome do arquivo
 * @param {string} fileSize - Tamanho formatado (ex: "2.5 MB")
 * @param {string} fileType - Tipo MIME do arquivo
 *
 * @example
 * <ConfirmDeleteModal ...>
 *   <FileDeleteContent
 *     fileName="documento.pdf"
 *     fileSize="2.5 MB"
 *     fileType="application/pdf"
 *   />
 * </ConfirmDeleteModal>
 */
export interface FileDeleteContentProps {
  fileName?: string;
  /** Rótulo exibido (fallback para `fileName`). Mostra a descrição em vez do
   *  nome de storage/UUID — ex.: catálogo de proposta. O badge de extensão
   *  continua derivando de `fileName`. */
  displayName?: string;
  fileSize?: string;
  fileType?: string;
}

export function FileDeleteContent({ fileName, displayName, fileSize, fileType }: FileDeleteContentProps) {
  const ext = fileName?.split('.').pop()?.toUpperCase() || 'FILE';

  const getTagColors = () => {
    if (fileType?.startsWith('image/')) {
      return { bg: '#ddd6fe', color: '#7c3aed' };
    }
    if (fileType === 'application/pdf') {
      return { bg: '#fecaca', color: '#dc2626' };
    }
    if (fileType?.includes('xml')) {
      return { bg: '#bbf7d0', color: '#16a34a' };
    }
    return { bg: '#e5e7eb', color: '#6b7280' };
  };

  const { bg, color } = getTagColors();

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        background: bg,
        color: color,
        borderRadius: '4px',
        fontSize: '10px',
        fontWeight: 700,
        textTransform: 'uppercase',
        flexShrink: 0
      }}>
        {ext}
      </span>
      <div>
        <div style={{ fontWeight: 500, wordBreak: 'break-word', fontSize: '14px' }}>
          {displayName ?? fileName}
        </div>
        {fileSize && (
          <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
            {fileSize}
          </div>
        )}
      </div>
    </div>
  );
}
